import { defineBoot } from '@quasar/app-vite/wrappers';
import _camelCase from 'lodash/camelCase';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import { Notify, useMeta } from 'quasar';
import errorService from 'src/services/error';
import storage from 'src/services/storage';
import store from 'src/store';

export class RouteGuard {
  static initialized = false;

  static loadStarted = false;

  /**
   *
   * @param {Object} params
   * @param {import('node_modules/vue-router/types/router').VueRouter} params.router
   * @param {RootStore} params.store
   */
  static init({ router, store }) {
    // navigation isn't triggered on app boot, so check for redirect onReady
    router
      .isReady()
      .then(() => {
        this.onReadySuccessHandlerFactory({ store });
      })
      .catch((...args) => {
        this.onReadyErrorHandler(...args);
      });

    // verify auth and permissions on route change
    router.beforeEach(this.guardRouteFactory({ router, store }));
  }

  static onReadySuccessHandlerFactory({ store }) {
    store.dispatch('app/routerReady');
    return () => {};
  }

  /**
   * Notifies user of unexpected boot error and logs the error.
   *
   * @param {Error} error
   */
  static onReadyErrorHandler(error) {
    errorService.notifyUnexpected(error);
  }

  /**
   * @param {{ router: import('src/router/typedefs').VueRouter; store: import('src/types/store').RootStore }} payload
   */
  static guardRouteFactory({ router, store }) {
    return async (to, from, next) => {
      // Handle logout redirect first
      const logoutRedirect = storage.get('session/logoutRedirect');
      storage.remove('session/logoutRedirect');
      if (logoutRedirect) {
        return next({ path: logoutRedirect, replace: from.name === 'loading' });
      }

      // initialize support sessions
      if (to.name === 'support') {
        if (to.params.accountId) {
          storage.set('session/accountId', to.params.accountId);
        }
        return next({ name: 'default' });
      }

      const isIndividual = store.getters['session/currentUser'].isIndividual();

      // handle default route
      if (to.name === 'default') {
        let redirectTo = storage.get('session/redirectTo') || storage.get('session/routeAfterLoad');
        storage.remove('session/redirectTo');
        storage.remove('session/routeAfterLoad');

        if (redirectTo) {
          const url = new URL(redirectTo, `${location.protocol}//${location.host}`);
          return next({
            path: url.pathname,
            query: Object.fromEntries(url.searchParams),
            replace: from.name === 'loading',
          });
        }

        if (isIndividual) {
          return next({ name: 'feed' });
        }

        return next({ name: 'map' });
      }

      // prevent navigation away from iframe routes
      const fromMatched = _get(from, 'matched', []);
      if (fromMatched.some((route) => route.name === 'iframes')) {
        return next(false);
      }

      let isAuthenticated = store.getters['session/isAuthenticated'];
      const toMatched = _get(to, 'matched', []);
      const isAuthRequired = toMatched.some((route) => _get(route, 'meta.auth', false));

      this.setPageTitle(to, store);

      // route doesn't require authentication
      if (isAuthRequired === false) {
        if (this.loadStarted && to.name !== 'loading') {
          storage.remove('session/routeAfterLoad');
        }
        return next();
      }

      // if unauth'd, try to get the profile
      if (isAuthenticated === false) {
        await store.dispatch('session/profile');
        isAuthenticated = store.getters['session/isAuthenticated'];

        // if still unauthorized, redirect to login
        if (isAuthenticated === false) {
          storage.set('session/redirectTo', to.fullPath);
          return next({
            path: '/login',
            replace: true,
          });
        }
      }

      // verify that there's a selectedAccount
      const accountResult = await this.selectAccount({
        router,
        store,
        to,
        from,
      });
      if (accountResult !== true) {
        return next(accountResult);
      }

      // verify the user is allowed to login
      if (store.getters['session/hasPermission']('isAllowedToLogin') === false) {
        Notify.create({
          icon: 'error',
          color: 'negative',
          timeout: 0,
          closeBtn: true,
          message:
            'You do not have access to use this app based on your account role.  Please check with your account administrator.',
        });
        return next({
          name: 'denied',
          replace: true,
        });
      }

      // don't initialize data in iframe, minimal app context
      if (!to.path.startsWith('/frame') && !RouteGuard.initialized) {
        await this.initializeData(store);
      }

      // handle route-specific permissions
      const requiredPermissions = toMatched.reduce((requirements, route) => {
        if (_get(route, 'meta.permissions', false)) {
          return [...requirements, ..._get(route, 'meta.permissions', [])];
        }
        return requirements;
      }, []);
      if (requiredPermissions.length > 0) {
        const hasPermission = store.getters['session/hasPermission'];
        const isAllowed = requiredPermissions.every((permission) => hasPermission(_camelCase(permission)));
        if (isAllowed) {
          if (this.loadStarted) {
            storage.remove('session/routeAfterLoad');
          }
          return next();
        }

        if (isIndividual) {
          return hasPermission('isAllowedToCheckIn')
            ? next({ name: 'checkIn', replace: true })
            : next({ name: 'feed', replace: true });
        }

        // if you don't have permission to visit the default route
        if (to.path === '/' || !from.name) {
          return next({
            name: 'denied',
            replace: true,
          });
        }

        Notify.create({
          icon: 'error',
          color: 'negative',
          message: `You do not have the necessary permissions (${requiredPermissions.join(', ')}) for ${to.fullPath}`,
        });

        return next(false);
      }

      if (this.loadStarted) {
        storage.remove('session/routeAfterLoad');
      }

      return next();
    };
  }

  /**
   * Initializes various data stores.
   *
   * @param {Object} store
   */
  static async initializeData(store) {
    RouteGuard.initialized = true;

    // initialize vehicles
    if (_isEmpty(store.state.vehicles.vehicles)) {
      store.dispatch('vehicles/initData');
    }
    // Initialize users
    store.dispatch('users/init');

    // Initialize apps
    if (store.state.apps.apps === null) {
      store.dispatch('apps/getPublishedApps');
    }

    // Initialize connected apps
    if (store.state.apps.connectedApps === null) {
      store.dispatch('apps/getConnectedApps');
    }

    // Initialize groups
    if (store.state.groups.groups.length === 0) {
      store.dispatch('groups/getGroups');
    }

    // Initialize tags
    if (store.state.tags.tags.length === 0) {
      store.dispatch('tags/getTags');
    }

    // Initialize asset types
    if (_isEmpty(store.state.assets.assetTypes)) {
      store.dispatch('assets/getAssetTypes');
    }

    // Initialize shares
    if (_isEmpty(store.state.shares.shares)) {
      store.dispatch('shares/getAccountShares');
    }
  }

  /**
   * Selects an account (or uses the already selected one).
   *
   * @param {Object} payload
   * @param {Object} payload.router
   * @param {Object} payload.store
   * @param {Object} payload.to
   * @param {import('vue-router').RouteLocation} payload.from
   * @param {Function} payload.next
   * @returns {import('node_modules/vue-router/types/router').RawLocation | false | ((vm: import('node_modules/vue/types/vue').Vue) => any) | void}
   */
  static async selectAccount({ router, store, to, from }) {
    const hasSelectedAccount = store.getters['session/hasSelectedAccount'];

    if (hasSelectedAccount) {
      return true;
    }

    let accounts = store.getters['session/accounts'];
    if (accounts.length === 0) {
      accounts = await store.dispatch('session/getAccounts');
      if (accounts.error && accounts.metadata.statusCode !== 401) {
        return {
          name: 'app-boot-failure',
          replace: true,
        };
      }
    }

    // Determine if all accounts are locked/inactive
    const validationResults = this.validateAccounts(accounts);
    if (validationResults !== 'valid') {
      return {
        name: 'no-accounts',
        replace: true,
        query: {
          reason: validationResults,
        },
      };
    }

    let accountId;
    if (accounts.length > 1) {
      // init session with stored accountId if available
      const storedAccountId = storage.get('session/accountId');
      const accountIds = accounts.map((account) => account.id);
      if (!storedAccountId || accountIds.includes(storedAccountId) === false) {
        const selectAccountRoute = '/select-account';
        if (to.fullPath === selectAccountRoute) {
          return undefined;
        }
        storage.set('session/redirectTo', to.fullPath);
        return {
          path: selectAccountRoute,
          replace: true,
        };
      }
      accountId = storedAccountId;
    } else {
      // select the only account automatically
      accountId = _get(accounts, [[0], 'id'], '');
    }

    if (!store.getters['session/isCriticalDataLoaded']) {
      store.dispatch('session/init', accountId).catch((error) => {
        errorService.log(error, {
          metaData: {
            'accountId Details': {
              'Account ID': accountId,
              'Local Storage Account': storage.get('session/accountId'),
              'Accounts JSON': JSON.stringify(accounts),
            },
          },
        });
        if (error.name !== 'AccountIdMissingError') {
          router.push({
            name: 'app-boot-failure',
            replace: true,
          });
        }
      });

      // Keep track of latest navigation while app is still loading
      if (!storage.get('session/routeAfterLoad') && from?.name !== 'loading') {
        storage.set('session/routeAfterLoad', to.fullPath);
      }

      this.loadStarted = true;

      return {
        name: 'loading',
        replace: true,
      };
    }
    return true;
  }

  /**
   * Validates that the user is associated to at least one valid account.
   *
   * @param {Account[]} accounts
   * @returns {"valid" | "all-locked" | "all-inactive" | "locked-or-inactive" | "no-accounts"}
   */
  static validateAccounts(accounts) {
    let locked = 0;
    let inactive = 0;

    if (!_isArray(accounts)) {
      return 'no-accounts';
    }

    const noAccounts = accounts.every((account) => {
      if (account.locked) {
        locked += 1;
        return true;
      }

      if (account.status !== 'Active') {
        inactive += 1;
        return true;
      }

      return false;
    });

    if (noAccounts) {
      //   let reason;
      if (locked === accounts.length) {
        return 'all-locked';
      }
      if (inactive === accounts.length) {
        return 'all-inactive';
      }
      return 'locked-or-inactive';
    }

    return 'valid';
  }

  /**
   * @param {import('vue-router').RouteLocation} to
   * @param {import('src/types/store').RootStore} store
   */
  static setPageTitle(to, store) {
    const assetVehicleMatches = to?.matched?.filter(({ name }) => ['asset', 'vehicle'].includes(name));
    if (assetVehicleMatches?.length) {
      const [{ name }] = assetVehicleMatches;
      const assetVehicle = store.state[`${name}s`][`${name}s`][to.params.key];
      if (!assetVehicle) {
        store.dispatch('app/setMobileTitlePending', true);
      }
    }

    if (to.meta?.title) {
      useMeta({
        title: `${to.meta.title} - ${store.getters['session/partnerName']}`,
      });
    }

    const mobileTitle = to.meta?.mobileTitle || '';
    store.dispatch('app/setMobileTitle', mobileTitle);
  }
}

export default defineBoot(async ({ router }) => {
  await RouteGuard.init({ router, store: store() });
});
